// https://github.com/rewardz/ric_constants/blob/master/ric_constants/permission_types.py

import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action
} from "vuex-module-decorators";
import store from "@/admin/store";

import { RolePermissionApi } from "../../../../gen/authapi/api";
import LocalStorage from "@/general/utils/storage";

@Module({
  name: "rolePermissions",
  store: store,
  namespaced: true,
  dynamic: true
})
class RolePermissionsModule extends VuexModule {
  permissions: any = {
    LOADED: false,

    CSVDownload: {
      // List
      // Create
      Read: {
        Group: false, // 910101
        GroupPoint: false, // 910201
        User: false, // 910301
        UserPoint: false, // 910401
        PointExpiration: false, // 910501
        GrantCriteria: false, // 910601
        GrantPoint: false, // 910701
        ThanksPoint: false, // 910801
        UsePoint: false, // 910901
        ProductApplication: false, // 911001
        UploadError: false, // 911101
        TaxDifference: false // 911201
      }
      // Update
      // Delete
    },

    CSVUpload: {
      // List
      Create: {
        UserRegistration: false, // 810101
        GroupRegistration: false, // 810201
        UserGroupRegistration: false, // 810301
        GrantCriteria: false, // 810401
        GrantPoint: false, // 810501
        ThanksGrantPoint: false, // 810601
        GrantGrantPoint: false, // 810701
        ResetPassword: false, // 811501
        ThanksPointApprovalDenial: false, // 811601
        ChangeUserInfo: false, // 810801
        UserGroups: false, // 810901
        UserInfo: false, // 811301
        UserId: false, // 811001
        DeleteUserGroup: false, // 811101
        DeleteUser: false, // 811201
        DeletePoint: false // 811401
      }
      // Read
      // Update
      // Delete
    },

    Dashboard: {
      // List
      // Create
      Read: {
        Overall: false, // 110101
        Group: false, // 110201
        User: false // 110301
      }
      // Update
      // Delete
    },

    GrantCriteria: {
      List: {
        TypeSearch: false, // 430101
        Sorting: false // 430102
      },
      Create: {
        Details: false, // 430201
        Groups: false // 430202
      },
      Read: {
        Details: false, // 430201
        Groups: false // 430202
      },
      Update: {
        Details: false, // 430201
        Groups: false // 430202
      },
      Delete: false // 430201
    },

    GrantPoint: {
      List: false, // 420101
      Create: {
        NoLimit: false, //410101
        Thanks: false, // 410102
        Grant: false // 410103
      },
      Read: false, // 420201
      // Update
      Delete: false // 420201
    },

    Site: {
      // List
      // Create
      Read: false, // 10101
      Update: false // 10101
      // Delete
    },

    ThanksPoint: {
      List: {
        Default: false, // 440101
        History: false // 440103
      },
      // Create
      Read: {
        Default: false, // 440201
        History: false // 440301
      },
      Update: {
        ApproveDenial: false // 440102
      }
      // Delete
    },

    WhatsNew: {
      List: false, // 510101
      Create: false, // 510201
      Read: false, // 510201
      Update: false, // 510201
      Delete: false // 510201
    }
  };

  get hasPermission() {
    return (keys: Array<string>) => {
      return keys.reduce(
        (value: any, key: string) => value[key],
        this.permissions
      );
    };
  }

  get hasOnePermissions() {
    return (keys: Array<Array<string>>) => {
      for (let i = 0; i < keys.length; i++) {
        if (
          keys[i].reduce(
            (value: any, key: string) => value[key],
            this.permissions
          ) === true
        ) {
          return true;
        }
      }
      return false;
    };
  }

  get hasAllPermissions() {
    return (keys: Array<Array<string>>) => {
      for (let i = 0; i < keys.length; i++) {
        if (
          keys[i].reduce(
            (value: any, key: string) => value[key],
            this.permissions
          ) === false
        ) {
          return false;
        }
      }
      return true;
    };
  }

  @Mutation
  setPermissions(permissions: any) {
    permissions.LOADED = true;
    this.permissions = permissions;
  }

  @Action({ rawError: true })
  async loadPermissions() {
    if (this.permissions.LOADED) {
      return;
    }

    if (LocalStorage.getItem("USER_GROUP_ROLE_PERMISSIONS") !== null) {
      // Load From LocalStorage
      const permissions = JSON.parse(
        LocalStorage.getItem("USER_GROUP_ROLE_PERMISSIONS") as string
      );

      this.context.commit("setPermissions", permissions);
    } else {
      // Load the API
      await this.context.dispatch("getAllPermissions");
    }
  }

  @Action({ rawError: true })
  async getAllPermissions() {
    if (LocalStorage.getItem("USER_GROUP_ID") === null) {
      await this.context.dispatch("notAllowed");
      return;
    }

    const groupId = LocalStorage.getItem("USER_GROUP_ID") as string;
    const Token = LocalStorage.getItem("AccessToken");

    const rolePermissionApi = new RolePermissionApi();

    const rolePermissionApiResponse = await rolePermissionApi.rolePermissionList(
      groupId,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { headers: { Authorization: " Bearer " + Token } }
    );

    if (
      rolePermissionApiResponse &&
      rolePermissionApiResponse.data &&
      rolePermissionApiResponse.data.results
    ) {
      const permissions: any = {};
      rolePermissionApiResponse.data.results.forEach((P: any) => {
        permissions[P.permission] = {
          C: P.create,
          R: P.read,
          U: P.update,
          D: P.delete
        };
      });

      // Process the Role permissions - mapping
      const getPermission = (NUM: string, OP: string) => {
        try {
          return permissions[NUM][OP];
        } catch (e) {
          return false;
        }
      };

      const mappedPermissions = {
        CSVDownload: {
          Read: {
            Group: getPermission("910101", "R"),
            GroupPoint: getPermission("910201", "R"),
            User: getPermission("910301", "R"),
            UserPoint: getPermission("910401", "R"),
            PointExpiration: getPermission("910501", "R"),
            GrantCriteria: getPermission("910601", "R"),
            GrantPoint: getPermission("910701", "R"),
            ThanksPoint: getPermission("910801", "R"),
            UsePoint: getPermission("910901", "R"),
            ProductApplication: getPermission("911001", "R"),
            UploadError: getPermission("911101", "R"),
            TaxDifference: getPermission("911201", "R")
          }
        },
        CSVUpload: {
          Create: {
            UserRegistration: getPermission("810101", "C"),
            GroupRegistration: getPermission("810201", "C"),
            UserGroupRegistration: getPermission("810301", "C"),
            GrantCriteria: getPermission("810401", "C"),
            GrantPoint: getPermission("810501", "C"),
            ThanksGrantPoint: getPermission("810601", "C"),
            GrantGrantPoint: getPermission("810701", "C"),
            ResetPassword: getPermission("811501", "C"),
            ThanksPointApprovalDenial: getPermission("811601", "C"),
            ChangeUserInfo: getPermission("810801", "C"),
            UserGroups: getPermission("810901", "C"),
            UserInfo: getPermission("811301", "C"),
            UserId: getPermission("811001", "C"),
            DeleteUserGroup: getPermission("811101", "C"),
            DeleteUser: getPermission("811201", "C"),
            DeletePoint: getPermission("811401", "C")
          }
        },

        Dashboard: {
          Read: {
            Overall: getPermission("110101", "R"),
            Group: getPermission("110201", "R"),
            User: getPermission("110301", "R")
          }
        },

        GrantCriteria: {
          List: {
            TypeSearch: getPermission("430101", "R"),
            Sorting: getPermission("430102", "U")
          },
          Create: {
            Details: getPermission("430201", "C"),
            Groups: getPermission("430202", "C")
          },
          Read: {
            Details: getPermission("430201", "R"),
            Groups: getPermission("430202", "R")
          },
          Update: {
            Details: getPermission("430201", "U"),
            Groups: getPermission("430202", "U")
          },
          Delete: getPermission("430201", "D")
        },

        GrantPoint: {
          List: getPermission("420101", "R"),
          Create: {
            NoLimit: getPermission("410101", "C"),
            Thanks: getPermission("410102", "C"),
            Grant: getPermission("410103", "C")
          },
          Read: getPermission("420201", "R"),
          Delete: getPermission("420201", "D")
        },

        Site: {
          Read: getPermission("10101", "R"),
          Update: getPermission("10101", "U")
        },

        ThanksPoint: {
          List: {
            Default: getPermission("440101", "R"),
            History: getPermission("440103", "R")
          },
          Read: {
            Default: getPermission("440201", "R"),
            History: getPermission("440301", "R")
          },
          Update: {
            ApproveDenial: getPermission("440102", "U")
          }
        },

        WhatsNew: {
          List: getPermission("510101", "R"),
          Create: getPermission("510201", "C"),
          Read: getPermission("510201", "R"),
          Update: getPermission("510201", "U"),
          Delete: getPermission("510201", "D")
        }
      };

      // const orgAdminSettings = LocalStorage.getItem("orgAdminSettings")
      //   ? JSON.parse(LocalStorage.getItem("orgAdminSettings") as string)
      //   : [{}];

      const userDetails = LocalStorage.getItem("userDetails");
      // if (orgAdminSettings && orgAdminSettings.length > 0) {
      if (userDetails) {
        // const settings = orgAdminSettings[0];
        const settings = JSON.parse(userDetails);

        if (
          settings.thanks_point_enabled === true &&
          settings.thanks_point_approval_enabled === true &&
          settings.thanks_exclusive_points_enabled === false
        ) {
          // Pattern 2 = Create ThanksExclusive GrantPoint + CSV Upload THanksOnly Point
          mappedPermissions["GrantPoint"]["Create"]["Thanks"] = false;
          mappedPermissions["CSVUpload"]["Create"]["ThanksGrantPoint"] = false;
        } else if (
          settings.thanks_point_enabled === true &&
          settings.thanks_point_approval_enabled === false &&
          settings.thanks_exclusive_points_enabled === true
        ) {
          // Pattern 3 = ThanksPoint Management all disable  + CSV UPload THanksPoint Approval & Denial
          mappedPermissions["ThanksPoint"]["List"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["List"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Update"]["ApproveDenial"] = false;

          mappedPermissions["CSVUpload"]["Create"][
            "ThanksPointApprovalDenial"
          ] = false;
        } else if (
          settings.thanks_point_enabled === true &&
          settings.thanks_point_approval_enabled === false &&
          settings.thanks_exclusive_points_enabled === false
        ) {
          // Pattern 4 = pattern 2 + pattern 3
          mappedPermissions["GrantPoint"]["Create"]["Thanks"] = false;
          mappedPermissions["CSVUpload"]["Create"]["ThanksGrantPoint"] = false;

          mappedPermissions["ThanksPoint"]["List"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["List"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Update"]["ApproveDenial"] = false;
          mappedPermissions["CSVUpload"]["Create"][
            "ThanksPointApprovalDenial"
          ] = false;
        } else if (
          settings.thanks_point_enabled === false &&
          settings.thanks_point_approval_enabled === false &&
          settings.thanks_exclusive_points_enabled === false
        ) {
          // Pattern 5 = Pattern 2 + Pattern 3 + CSV Download ThanksGiving Point Data
          mappedPermissions["GrantPoint"]["Create"]["Thanks"] = false;
          mappedPermissions["CSVUpload"]["Create"]["ThanksGrantPoint"] = false;

          mappedPermissions["ThanksPoint"]["List"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["List"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["Default"] = false;
          mappedPermissions["ThanksPoint"]["Read"]["History"] = false;
          mappedPermissions["ThanksPoint"]["Update"]["ApproveDenial"] = false;
          mappedPermissions["CSVUpload"]["Create"][
            "ThanksPointApprovalDenial"
          ] = false;

          mappedPermissions["CSVDownload"]["Read"]["ThanksPoint"] = false;
        }

        if (settings.grant_exclusive_points_enabled === false) {
          // create GrantExclusive GrantPoint
          // CSV Upload GrantOnly
          mappedPermissions["GrantPoint"]["Create"]["Grant"] = false;
          mappedPermissions["CSVUpload"]["Create"]["GrantGrantPoint"] = false;
        }
      }

      // Write to the LocalStorage
      LocalStorage.setItem(
        "USER_GROUP_ROLE_PERMISSIONS",
        JSON.stringify(mappedPermissions)
      );

      this.context.commit("setPermissions", mappedPermissions);
    }
  }

  @Action
  notAllowed() {
    if (!LocalStorage.getItem("AccessToken")) {
      LocalStorage.setItem("ON_LOGIN_REDIRECT", window.location.href);
    }
    window.location.href = "/system-error/";
  }
}

export default getModule(RolePermissionsModule);
